import { useState } from "react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api } from "../lib/api";
import { COUNTRIES, SPECIALTY_OPTIONS } from "../data/site";
import FileUploader from "./FileUploader";
import WhatsAppButton from "./WhatsAppButton";
import { waMessageFor } from "../lib/whatsapp";
import { Clock3, ShieldCheck, Stethoscope, ArrowRight } from "lucide-react";

export default function SecondOpinionBlock() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "", specialty: "", condition: "",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.country || !form.specialty) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/second-opinion", { ...form, file_ids: files.map((f) => f.id) });
      setDone(true);
      toast.success("We have received your request. A specialist review will be arranged.");
    } catch {
      toast.error("Unable to submit. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="second-opinion-section" className="relative bg-[#0B1F3A] text-white py-24 lg:py-32 overflow-hidden grain">
      <div className="absolute inset-x-0 top-0 gold-rule opacity-60" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5">
          <div className="overline text-[#C8A96A]">Signature Offering</div>
          <h2 className="mt-6 font-serif font-light text-white text-4xl lg:text-6xl leading-[1.02] tracking-tight">
            A Second Opinion,<br/>within <em className="not-italic text-[#C8A96A]">48 hours.</em>
          </h2>
          <p className="mt-8 text-[15px] leading-[1.9] text-white/75 max-w-lg">
            Share your existing reports privately. A senior specialist from our network
            will review them confidentially and prepare a considered written opinion —
            delivered to you inside two business days. No obligation. No cost for the
            initial review.
          </p>
          <ul className="mt-10 space-y-4 text-[13px] text-white/80">
            <li className="flex gap-3"><Clock3 size={18} strokeWidth={1.25} className="text-[#C8A96A] shrink-0 mt-0.5"/> Written opinion in 48 hours</li>
            <li className="flex gap-3"><Stethoscope size={18} strokeWidth={1.25} className="text-[#C8A96A] shrink-0 mt-0.5"/> Senior consultant · 15+ years' practice</li>
            <li className="flex gap-3"><ShieldCheck size={18} strokeWidth={1.25} className="text-[#C8A96A] shrink-0 mt-0.5"/> Discretion assured · reports encrypted</li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="second-opinion-success" className="bg-white text-[#0B1F3A] p-10 lg:p-14">
              <div className="overline text-[#C8A96A]">Received</div>
              <h3 className="mt-4 font-serif text-4xl">Your reports are in safe hands.</h3>
              <p className="mt-5 text-[14px] leading-relaxed text-[#0B1F3A]/70 max-w-md">
                A concierge will confirm receipt shortly and coordinate the specialist review.
                Please look for a private note from <span className="text-[#0B1F3A]">concierge@easyilaaz.com</span>,
                or continue instantly on WhatsApp.
              </p>
              <div className="mt-8">
                <WhatsAppButton
                  variant="primary"
                  testId="so-success-whatsapp"
                  message={waMessageFor({ name: form.full_name, specialty: form.specialty, country: form.country, kind: "second_opinion" })}
                >
                  Continue on WhatsApp
                </WhatsAppButton>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="second-opinion-form"
              className="bg-white text-[#0B1F3A] p-8 lg:p-10"
            >
              <div className="overline text-[#C8A96A]">Request a Second Opinion</div>
              <h3 className="mt-3 font-serif text-3xl">Begin — privately.</h3>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input data-testid="so-name" placeholder="Full name *" value={form.full_name} onChange={set("full_name")} />
                <Input data-testid="so-email" type="email" placeholder="Email *" value={form.email} onChange={set("email")} />
                <Input data-testid="so-phone" placeholder="Phone with country code *" value={form.phone} onChange={set("phone")} />
                <Select value={form.country} onValueChange={set("country")}>
                  <SelectTrigger data-testid="so-country"><SelectValue placeholder="Country of residence *" /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.specialty} onValueChange={set("specialty")}>
                  <SelectTrigger data-testid="so-specialty" className="md:col-span-2"><SelectValue placeholder="Specialty *" /></SelectTrigger>
                  <SelectContent>{SPECIALTY_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="so-condition" placeholder="Condition or working diagnosis (optional)" value={form.condition} onChange={set("condition")} className="md:col-span-2" />
              </div>

              <div className="mt-5">
                <FileUploader files={files} setFiles={setFiles} testId="so-uploader" />
              </div>

              <button
                data-testid="so-submit"
                type="submit"
                disabled={submitting}
                className="mt-8 inline-flex items-center gap-3 h-12 px-8 rounded-full bg-[#0B1F3A] text-white text-[12px] tracking-[0.18em] uppercase hover:bg-[#C8A96A] transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Request Second Opinion"} <ArrowRight size={16} strokeWidth={1.25} />
              </button>
              <p className="mt-4 text-[11px] leading-relaxed text-[#0B1F3A]/50 max-w-md">
                By requesting a second opinion you consent to Easyilaaz sharing your reports
                only with the reviewing specialist in strict confidence.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
